/**
 * ADM-INTEGRATION-ENTRA.MODAL
 * Preview of the import of the Entra ID directory (Cryptr), then the import and the users not imported
 */
import { store } from "@/index"
import { useEffect, useState } from "react"
import { integrationImportEntra } from "@/redux/integration.actions"
import { sessionEditAccountOptions } from "@/redux/_session.actions"
import { roleActions } from "@/redux/role.reducers"
import LoadingModal from "./loading.modal"
import Modal from "@/components/modal"
import ListItem from "@/components/list-item"
import ListCell from "@/components/list-cell"
import Tooltip from "@/components/tooltip"
import Checkbox from "@/components/checkbox"
import ListFields from "@/components/list-fields"

interface OwnProps {
  onClose: Function
  onNext: Function
}

interface EntraPreview {
  insert: number
  update: number
  delete: number
  fields: { importName: string; name: string; count: number; sample: string | null }[]
}

function AdmIntegrationEntraModal(props: OwnProps) {
  const [preview, setPreview] = useState<EntraPreview | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  //Users of the directory missing from the account after the import
  const [notImported, setNotImported] = useState<{ email: string; firstname: string; lastname: string }[] | null>(
    null
  )
  //Last choice of the admin, repeated by the scheduled imports
  const [isManagerAccess, setIsManagerAccess] = useState(store.getState()._session.accountOptions.entraManagerAccess ?? true)

  //On load: what the import will do, nothing is saved
  useEffect(() => {
    load()

    async function load() {
      const response: any = await store.dispatch(integrationImportEntra(true, false))
      if (response.error) {
        props.onClose()
      } else {
        setPreview(response)
      }
    }
  }, [])

  const hasManager = Boolean(preview?.fields.some((field) => field.importName === "manager" && field.count))

  async function next() {
    setIsImporting(true)
    const response: any = await store.dispatch(integrationImportEntra(false, isManagerAccess && hasManager))
    setIsImporting(false)
    if (response.error) return

    //The role "Managers" may be created by the import: the roles are fetched again
    store.dispatch(sessionEditAccountOptions("entraManagerAccess", isManagerAccess && hasManager))
    store.dispatch(roleActions.reset())
    setNotImported(response.notImported)
  }

  if (notImported) {
    return (
      <Modal isLarge onClose={() => props.onNext()} onNext={() => props.onNext()} onNextCta="OK" title="Import terminé">
        {notImported.length ? (
          <>
            <p>{notImported.length} collaborateur(s) n'ont pas été importés :</p>
            <div className="h-10" />
            {notImported.map((user, index) => (
              <ListItem key={index}>
                <ListCell text={user.firstname} width={160} />
                <ListCell text={user.lastname} width={160} />
                <ListCell isFlex text={user.email || "Pas d'adresse email"} />
              </ListItem>
            ))}
          </>
        ) : (
          <p>Tous les collaborateurs ont été importés.</p>
        )}
      </Modal>
    )
  }

  return !preview || isImporting ? (
    <LoadingModal>{isImporting ? "Import en cours, cela peut durer quelques minutes…" : undefined}</LoadingModal>
  ) : (
    <Modal
      isCloseButtonVisible
      isLarge
      onClose={() => props.onClose()}
      onNext={next}
      title="Champs importés depuis Entra ID">
      <p>
        {preview.insert} collaborateur(s) à ajouter, {preview.update} à mettre à jour, {preview.delete} à supprimer.
      </p>

      <div className="h-10" />

      <Tooltip text={hasManager ? null : "L'attribut Manager n'est renseigné pour aucun collaborateur."}>
        <Checkbox
          active={isManagerAccess && hasManager}
          disabled={!hasManager}
          onClick={() => setIsManagerAccess(!isManagerAccess)}
          text="Utiliser l'attribut manager pour créer les accès observateurs."
        />
      </Tooltip>

      <div className="h-10" />

      <ListFields>
        <ListCell text="Attribut" width={200} />
        <ListCell isFlex text="Valeurs" />
      </ListFields>

      {preview.fields.map((field) => (
        <ListItem key={field.importName}>
          {field.count ? (
            <>
              <ListCell isBold text={field.name} width={200} />
              <ListCell isFlex text={field.sample} />
            </>
          ) : (
            <Tooltip text="Ce champ n'est renseigné pour aucun collaborateur.">
              <ListCell className="grey-t" isBold text={field.name} width={200} />
            </Tooltip>
          )}
        </ListItem>
      ))}
    </Modal>
  )
}

export default AdmIntegrationEntraModal
